import { isLocaleTag } from './product-form'

/**
 * Locale-settings editor state (TO-416 product-admin-ui REQ-009), mirroring the
 * API's subset rules (TO-415 tenant-locale-settings REQ-002).
 */
export interface LocaleSettingsValues {
	enabledLocales: string[]
	requiredLocales: string[]
	defaultLocale: string | null
}

export interface LocaleSettingsState extends LocaleSettingsValues {
	/** The last refused `add`, as an error key. */
	error: string | null
}

export type LocaleSettingsAction =
	| { type: 'add'; locale: string }
	| { type: 'remove'; locale: string }
	| { type: 'toggleRequired'; locale: string }
	| { type: 'setDefault'; locale: string }
	| { type: 'load'; settings: LocaleSettingsValues }

export const EMPTY_LOCALE_SETTINGS: LocaleSettingsState = {
	enabledLocales: [],
	requiredLocales: [],
	defaultLocale: null,
	error: null,
}

const MAX_LOCALES = 20

const addError = (state: LocaleSettingsState, locale: string): string | null => {
	if (!isLocaleTag(locale)) return 'localeSettings.errors.tag'
	if (state.enabledLocales.includes(locale)) return 'localeSettings.errors.duplicate'
	if (state.enabledLocales.length >= MAX_LOCALES) return 'localeSettings.errors.max'
	return null
}

const add = (state: LocaleSettingsState, locale: string): LocaleSettingsState => {
	const error = addError(state, locale)
	return error ? { ...state, error } : { ...state, enabledLocales: [...state.enabledLocales, locale], error: null }
}

const remove = (state: LocaleSettingsState, locale: string): LocaleSettingsState => ({
	...state,
	enabledLocales: state.enabledLocales.filter((l) => l !== locale),
	requiredLocales: state.requiredLocales.filter((l) => l !== locale),
	defaultLocale: state.defaultLocale === locale ? null : state.defaultLocale,
})

const toggleRequired = (state: LocaleSettingsState, locale: string): LocaleSettingsState => ({
	...state,
	requiredLocales: state.requiredLocales.includes(locale)
		? state.requiredLocales.filter((l) => l !== locale)
		: [...state.requiredLocales, locale],
})

export const localeSettingsReducer = (
	state: LocaleSettingsState,
	action: LocaleSettingsAction,
): LocaleSettingsState => {
	switch (action.type) {
		case 'add':
			return add(state, action.locale)
		case 'remove':
			return remove(state, action.locale)
		case 'toggleRequired':
			return toggleRequired(state, action.locale)
		case 'setDefault':
			return { ...state, defaultLocale: action.locale }
		case 'load':
			return { ...action.settings, error: null }
	}
}

/** The blocking problems checked before PUT; empty when the settings may be saved. */
export const localeSettingsProblems = (state: LocaleSettingsState): string[] => {
	const problems: string[] = []
	if (state.enabledLocales.length === 0) problems.push('localeSettings.errors.atLeastOne')
	if (state.requiredLocales.some((l) => !state.enabledLocales.includes(l))) {
		problems.push('localeSettings.errors.requiredSubset')
	}
	if (state.defaultLocale === null || !state.enabledLocales.includes(state.defaultLocale)) {
		problems.push('localeSettings.errors.defaultRequired')
	}
	return problems
}
